import {
  AsYouType,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";

const asCountry = (countryCode: string | null | undefined): CountryCode | undefined => {
  if (!countryCode) return undefined;
  const code = countryCode.toUpperCase();
  try {
    getCountryCallingCode(code as CountryCode);
    return code as CountryCode;
  } catch {
    return undefined;
  }
};

/** National example, grouped the same way `formatNationalInput` groups keystrokes. */
export const phoneInputPlaceholder = (countryCode: string): string => {
  const country = asCountry(countryCode);
  if (!country) return "";
  return getExampleNumber(country, examples)?.formatNational() ?? "";
};

/** Calling code shown beside the flag, e.g. `+52`. */
export const dialCodeForCountry = (countryCode: string): string => {
  const country = asCountry(countryCode);
  if (!country) return "+";
  return `+${getCountryCallingCode(country)}`;
};

/** Digits of a valid example. Used to seed gallery states. */
export const exampleNationalDigits = (countryCode: string): string => {
  const country = asCountry(countryCode);
  if (!country) return "";
  return getExampleNumber(country, examples)?.nationalNumber ?? "";
};

/**
 * Group national digits the way the country's plan does.
 * Extra digits past the plan are dropped, so the field cannot grow past a real number.
 */
export const formatNationalInput = (countryCode: string, raw: string): string => {
  const country = asCountry(countryCode);
  const digits = raw.replace(/\D/g, "");
  if (!country || !digits) return digits;
  let accepted = "";
  let formatted = "";
  for (const digit of digits) {
    if (accepted.length >= 15) break;
    const next = accepted + digit;
    const candidate = new AsYouType(country).input(next);
    const lostTemplate =
      next.length > 3 && !/\D/.test(candidate) && /\D/.test(formatted);
    if (lostTemplate) break;
    accepted = next;
    formatted = candidate;
  }
  return formatted;
};

/** True when the national number is a valid number for that country. */
export const isNationalPhoneValid = (countryCode: string, value: string): boolean => {
  const country = asCountry(countryCode);
  const digits = value.replace(/\D/g, "");
  if (!country || !digits) return false;
  return parsePhoneNumberFromString(digits, country)?.isValid() ?? false;
};
